"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

interface LoanRequestDialogProps {
    listingId: string;
    listingTitle: string;
    ownerName: string;
}

export function LoanRequestDialog({ listingId, listingTitle, ownerName }: LoanRequestDialogProps) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const router = useRouter();

    const today = new Date().toISOString().split("T")[0];
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [message, setMessage] = useState("");

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            const res = await fetch("/api/loans", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    listingId,
                    startDate: new Date(startDate).toISOString(),
                    endDate: new Date(endDate).toISOString(),
                    message
                }),
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || "Erreur inconnue");
            }

            toast.success("Demande de prêt envoyée !");
            setOpen(false);
            // Redirect to messages to see the conversation
            const loan = await res.json();
            // Since API returns the loan object, we don't have owner ID directly here easily 
            // unless we fetched it or it's in response. 
            // But usually we want to go to the conversation.
            // Let's just refresh or let the user decide.
            // Better UX: Go to messages.
            router.push("/messages");

        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Erreur lors de la demande");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="w-full bg-gradient-to-r from-hp-purple to-hp-orange text-white shadow-hp hover:from-hp-purple-dark hover:to-hp-orange-dark rounded-xl">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    Demander un prêt
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Emprunter cet objet</DialogTitle>
                    <DialogDescription>
                        Proposez des dates à {ownerName} pour emprunter &quot;{listingTitle}&quot;.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="grid gap-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="start">Début</Label>
                            <Input
                                id="start"
                                type="date"
                                min={today}
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                required
                            />
                        </div>
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="end">Fin</Label>
                            <Input
                                id="end"
                                type="date"
                                min={startDate}
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label htmlFor="message">Message pour {ownerName}</Label>
                        <Textarea
                            id="message"
                            placeholder="Bonjour, je serais intéressé par..."
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            className="resize-none"
                            rows={4}
                        />
                    </div>

                    <DialogFooter>
                        <Button type="submit" disabled={loading} className="w-full bg-hp-purple hover:bg-hp-purple-dark text-white">
                            {loading ? <Loader2 className="animate-spin mr-2 h-4 w-4" /> : "Envoyer la demande"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
